import React, { useMemo, useRef } from "react";

import { IInputNumber, useInputNumber } from "../../hooks";
import { numberWithSeparator, useForkRef } from "../../utils";
import { EditableContent, EditableContentInterface } from "../EditableContent";
import { Tooltip } from "../Tooltip";

export interface EditableCurrencyProps
  extends EditableContentInterface,
    Omit<IInputNumber, "limit" | "onBlur"> {
  className?: string;
}

export const EditableCurrency = React.forwardRef<
  HTMLDivElement,
  EditableCurrencyProps
>(
  (
    {
      decimalLength = 2,
      decimalSeparator = ".",
      disableAbbreviation = false,
      disabled,
      disableMacros = false,
      step = 1,
      thousandSeparator = ",",
      value: defaultValue,
      onSave,
      onCancel,
      className,
      allowEmptyValue,
      onEnterKeyPress: externalEnterKeyPress,
      onClick: externalOnClick,
      onChange: externalOnChange,
      trimDecimals = true,
      min,
      max,
      invalid,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef(ref, inputRef);

    const {
      value,
      onBlur,
      onKeyDown,
      onChange,
      onClick,
      onDoubleClick,
      onFocus,
      focused,
      unformattedValue,
    } = useInputNumber(
      {
        decimalLength,
        decimalSeparator,
        disableAbbreviation,
        disableMacros,
        step,
        thousandSeparator,
        value: defaultValue,
        onSave,
        allowEmptyValue,
        onCancel,
        onChange: externalOnChange,
        onEnterKeyPress: externalEnterKeyPress,
        onClick: externalOnClick,
        trimDecimals,
        min,
        max,
        shortenThreshold: 10000,
      },
      inputRef
    );

    const formattedValue = useMemo(() => {
      return focused
        ? value
        : numberWithSeparator(value ?? "", "", decimalSeparator);
    }, [focused, value, decimalSeparator]);

    return (
      <Tooltip
        title={
          numberWithSeparator(
            unformattedValue ?? "",
            thousandSeparator,
            decimalSeparator
          ) as string
        }
        disable={focused}
      >
        <EditableContent
          disabled={disabled}
          ref={handleRef}
          className={className}
          invalid={invalid}
          inputProps={{
            value: formattedValue,
            onBlur: onBlur,
            onChange: onChange,
            onKeyDown: onKeyDown,
            onClick: onClick,
            onFocus: onFocus,
            onDoubleClick: onDoubleClick,
            ...rest,
          }}
        />
      </Tooltip>
    );
  }
);

EditableCurrency.displayName = "EditableCurrency";
